/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {
  EntityTemplate,
  TemplateColumn,
  TemplateOperators,
  TemplateValidation
} from '@ion/core/src/types/templates';
import {createValidation} from '@ion/core/src/xlsx/TemplateHelpers';

/**
 * Provides a utility function which manages the dynamic generation of an XLSX
 * spreadsheet specific to Asset File imports for download by the User.
 * 
 * Asset File Template column definitions are defined as localized entries within
 * the toolbox-modbus.json localization documents for each respective locale.
 * Entries take the following form:
 *
 * ```typescript
 *
 * import {assetFileTemplate} from '@ion/applications/src/components/modbus/ModBusParser';
 *
 * const templates = {
 *   [Entity.toolboxModbus] : assetFileTemplate
 * };
 * ```
 *
 * @param columns An array of localized asset file template column definitions.
 * @param options An array of other optional column definitions.
 */
export const assetFileTemplate = ({columns, workbook, options = {}}: EntityTemplate) => {
  const columnEntries: TemplateColumn[] = [];
  const columnValidations: TemplateValidation[] = [];
  const templateColumns = {};

  columns.forEach((column, index) => {
    const {key, type, required, error, width, style, header} = column || {};
    const validation = createValidation({required, error, index, type});

    if (error) {
      if (required) {
        validation.operator = TemplateOperators.Between;
      }
    }

    columnEntries.push({key, header, width, style});
    columnValidations.push(templateColumns[key]?.({column, validation, workbook, options}) || validation);
  });

  return {
    columnEntries,
    columnValidations
  };
};
